// nls-rework\src\app\components\loan\make-payment\make-payment.component.ts
import {
  Component,
  effect,
  inject,
  input,
  ChangeDetectorRef,
  signal,
  computed,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatStepperModule } from '@angular/material/stepper';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { CurrencyPipe, DatePipe, TitleCasePipe } from '@angular/common';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  FormGroup,
} from '@angular/forms';
import { RouterLink, ActivatedRoute } from '@angular/router';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import {
  switchMap,
  debounceTime,
  distinctUntilChanged,
  of,
  tap,
  Observable,
  map,
  combineLatest,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoanService } from '../../../services/loan.service';
import { PaymentService } from '../../../services/payment.service';
import { BankService } from '../../../services/bank.service';
import {
  PaymentType,
  AccountType,
  PaymentPeriod,
  RecurringPaymentDetails,
  PaymentAmountType,
  PaymentForm,
} from '../../../models/payment.model';
import { PopulateFormDirective } from '../../../directives/populate-form.directive';
import { BREAKPOINT_EXPANDED } from '../../../shared/constants/breakpoints.constants';
import { firstValueFrom } from 'rxjs';
import { ConfirmAddBankDialogComponent } from '../../../dialogs/confirm-add-bank-dialog/confirm-add-bank-dialog.component';
import { DialogService } from '../../../services/dialog/dialog.service';
import { Bank } from '../../../models/bank.model';
import { accountNumberValidator } from '../../../shared/utils/validators';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { ManagePaymentMethodsDialogComponent } from '../../../dialogs/manage-payment-methods-dialog/manage-payment-methods-dialog.component';
import { IpospayService } from '../../../services/ipospay.service';

export const INVALID_ACCOUNT_NUMBER_MSG =
  'Enter a valid account number (4-20 digits)';

// Expiry date validator
function expiryDateValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    // Validate MM/YY format
    if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(control.value)) {
      return { pattern: true };
    }

    const [month, year] = control.value.split('/');
    const expiry = new Date(2000 + parseInt(year), parseInt(month) - 1);
    const today = new Date();

    // Set current date to beginning of month for accurate comparison
    today.setDate(1);
    today.setHours(0, 0, 0, 0);

    if (expiry < today) {
      return { expired: true };
    }

    return null;
  };
}

// Validate that account number matches confirmation field
function accountNumberMatchValidator(
  accountNumberControl: AbstractControl
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const accountNumber = accountNumberControl.value;
    const accountNumberConfirm = control.value;

    if (
      accountNumber &&
      accountNumberConfirm &&
      accountNumber !== accountNumberConfirm
    ) {
      return { mismatch: true };
    }

    return null;
  };
}

/**
 * Component for making payments on loans
 */
declare let postData: () => Promise<{ payment_token_id?: string }>;

@Component({
  selector: 'app-make-payment',
  templateUrl: './make-payment.component.html',
  styleUrls: ['./make-payment.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    ReactiveFormsModule,
    CurrencyPipe,
    DatePipe,
    TitleCasePipe,
    RouterLink,
    PopulateFormDirective,
  ],
})
export class MakePaymentComponent implements OnInit {
  /** Injected services and dependencies */
  readonly #cdr = inject(ChangeDetectorRef);
  readonly #breakpointObserver = inject(BreakpointObserver);
  readonly #dialogService = inject(DialogService);
  readonly #loanService = inject(LoanService);
  readonly #fb = inject(FormBuilder);
  readonly #paymentService = inject(PaymentService);
  readonly #bankService = inject(BankService);
  readonly #route = inject(ActivatedRoute);
  readonly #snackbar = inject(MatSnackBar);
  readonly #ipospay = inject(IpospayService);

  private readonly dialog: MatDialog = inject(MatDialog);
  private paymentMethodDialog = inject(MatDialog);
  public disableSaveBank = false;
  private initialWalletId: number | null = null;

  /** Input for loan ID and associated data */
  readonly id = input.required<string>();
  readonly loan = toSignal(
    toObservable(this.id).pipe(
      switchMap((id) => this.#loanService.getLoanDetails(id))
    ),
    { initialValue: null }
  );

  /** Constant payment types and references */
  readonly PaymentType = PaymentType;
  readonly AccountType = AccountType;
  readonly PaymentPeriod = PaymentPeriod;
  readonly today = new Date();
  readonly PaymentAmountType = PaymentAmountType;
  readonly securityHintMessage =
    'This field cannot be edited for security reasons';
  readonly invalidAccountNumberMsg = INVALID_ACCOUNT_NUMBER_MSG;
  /** Card fee rate for one-time card payments */
  readonly cardFeeRate = 0.035;
  /** Flag to show/hide card payment option */
  public showCardOption = false;

  /** Bank lookup signals */
  readonly bankName = signal<string | null>(null);
  readonly isBankLoading = signal<boolean>(false);
  public banks: Bank[] = [];

  /** Stepper orientation based on screen size  */
  readonly stepperOrientation = toSignal(
    this.#breakpointObserver
      .observe(`(min-width: ${BREAKPOINT_EXPANDED}px)`)
      .pipe(map((result) => (result.matches ? 'horizontal' : 'vertical'))),
    { initialValue: 'vertical' as 'horizontal' | 'vertical' }
  );

  /** Payment form group */
  readonly paymentForm = this.#fb.group({
    // Payment Method
    type: [PaymentType.Bank, Validators.required],

    // Bank Account Details
    bankAccountOption: ['new', Validators.required],
    routingNumber: [''],
    accountNumber: [''],
    accountNumberConfirm: [''],
    accountType: [AccountType.Checking],
    existingBankId: [null as number | null, Validators.required],
    // Card (one-time only; not saved)
    cardName: [''],
    cardNumber: [''],
    cardExpiry: [''], // MM/YY
    cardCvv: [''],
    cardZip: [''],
    // Payment Info
    paymentType: ['oneTime', Validators.required],
    paymentMethod: ['bank'],
    amount: [''],
    oneTimeDate: [null as Date | null],
    recurringAmount: [''],
    frequency: [''],
    dayOfWeek: [''],
    monthlyStartDate: [null as Date | null],

    // Legacy fields for compatibility
    isRecurring: [false],
    dateRange: this.#fb.group({
      start: [null as Date | null],
      end: [null as Date | null],
    }),
    period: [PaymentPeriod.OneTime],
    amountType: [PaymentAmountType.Fixed],
  });

  private cardValidators = {
    name: [Validators.required],
    number: [Validators.required, cardPanValidator(/* requireLuhn? */ false)],
    expiry: [
      Validators.required,
      Validators.pattern(/^(0[1-9]|1[0-2])\/\d{2}$/),
    ],
    cvv: [Validators.required, Validators.pattern(/^\d{3,4}$/)],
    // US ZIP: 5 digits (or ZIP+4 if you want: /^\d{5}(-\d{4})?$/)
    zip: [Validators.required, Validators.pattern(/^\d{5}$/)],
  };

  /** Payment method signal (bank | card) */
  readonly paymentMethodSig = toSignal(
    this.paymentForm.get('paymentMethod')!.valueChanges,
    {
      initialValue: this.paymentForm.get('paymentMethod')!.value,
    }
  );

  /** Amount signal (string/number from form control) */
  readonly amountSig = toSignal(this.paymentForm.get('amount')!.valueChanges, {
    initialValue: this.paymentForm.get('amount')!.value,
  });

  /** Live total with fee when paying by card */
  readonly cardTotal = computed(() => {
    const method = this.paymentMethodSig();
    const raw = this.amountSig() as any;
    const amount = typeof raw === 'number' ? raw : parseFloat(raw || '0');
    if (!amount || Number.isNaN(amount)) return 0;
    return method === 'card'
      ? +(amount * (1 + this.cardFeeRate)).toFixed(2)
      : +(+amount).toFixed(2);
  });

  /** (Optional) fee amount alone if you need it elsewhere */
  readonly cardFeeAmount = computed(() => {
    const raw = this.amountSig() as any;
    const amount = typeof raw === 'number' ? raw : parseFloat(raw || '0');
    if (!amount || Number.isNaN(amount)) return 0;
    return +(amount * this.cardFeeRate).toFixed(2);
  });

  /** Payment state flags */
  paymentConfirmed = false;
  currentStep = 0;
  isProcessingPayment = false;
  isEditingExistingRecurringPayment = false;
  private routeWantsRecurringEdit = false;
  amountInputFocused = false;

  private editingOneTimePaymentId: number | null = null;

  isTokenizing = false;
  testTokenId: string | null = null;

  /** Lock/unlock the paymentType control based on billingType */
  private setPaymentTypeFromBillingType(
    billingType: string | null,
    autoPayParam?: string
  ) {
    const ctrl = this.paymentForm.get('paymentType');
    if (billingType == null || billingType === '') {
      ctrl?.enable({ emitEvent: false });
      return;
    }

    const n = Number(billingType);
    if (Number.isNaN(n)) {
      ctrl?.enable({ emitEvent: false });
      return;
    }

    let locked: string;
    if (n === 1) {
      locked = 'oneTime';
    } else if (n === 0) {
      locked = 'autoPay'; // Billing Amount autopay
    } else if (n === 2) {
      locked = 'recurringPayment'; // Fixed recurring autopay
    } else {
      locked = 'oneTime';
    }

    ctrl?.setValue(locked, { emitEvent: false });

    // Set isRecurring flag consistently for autopay/recurring
    if (locked !== 'oneTime') {
      this.paymentForm.patchValue({ isRecurring: true }, { emitEvent: false });
    }

    ctrl?.disable({ emitEvent: false });
  }

  /** Signals for form value changes */
  readonly paymentType = toSignal(
    this.paymentForm.get('paymentType')!.valueChanges,
    {
      initialValue: this.paymentForm.get('paymentType')!.value,
    }
  );

  /** Bank account option signal */
  readonly bankAccountOption = toSignal(
    this.paymentForm.get('bankAccountOption')!.valueChanges,
    {
      initialValue: this.paymentForm.get('bankAccountOption')!.value,
    }
  );

  /** Frequency signal */
  readonly frequency = toSignal(
    this.paymentForm.get('frequency')!.valueChanges,
    {
      initialValue: this.paymentForm.get('frequency')!.value,
    }
  );

  /** Recurring payment signal */
  readonly isRecurring = toSignal(
    this.paymentForm.get('isRecurring')!.valueChanges,
    {
      initialValue: this.paymentForm.get('isRecurring')!.value,
    }
  );

  /** Amount type signal */
  readonly amountType = toSignal(
    this.paymentForm.get('amountType')!.valueChanges,
    {
      initialValue: this.paymentForm.get('amountType')!.value,
    }
  );

  /** Single refresh of banks; optionally select a match */
  private async refreshBanksAndSelect(match?: (b: Bank) => boolean) {
    const banks = await firstValueFrom(this.#bankService.getBanks());
    this.banks = banks;

    this.disableSaveBank = banks.length > 0;

    // keep current selection if still valid
    const currentId = this.paymentForm.get('existingBankId')?.value as
      | number
      | null;
    if (currentId && !banks.some((b) => b.ACHWalletID === currentId)) {
      this.paymentForm.patchValue({ existingBankId: null });
    }

    if (match) {
      const found = banks.find(match);
      if (found?.ACHWalletID != null) {
        this.paymentForm.patchValue({
          existingBankId: found.ACHWalletID, // ⬅️ numeric
          bankAccountOption: 'existing',
        });
      }
    }
  }

  /** Poll until backend assigns an ACHWalletID (or timeout) */
  private async pollForACHWalletId(
    opts: { last2?: string; routing?: string; nickname?: string },
    timeoutMs = 30_000,
    intervalMs = 2_000
  ) {
    const deadline = Date.now() + timeoutMs;

    const match = (b: Bank) => {
      // try several "fingerprints" to find the newly created wallet
      const last2 = ('' + b.AccountNumber).slice(-2);
      const nickOk = opts.nickname
        ? b.bankName?.includes(opts.nickname) ?? false
        : true;
      const lastOk = opts.last2 ? last2 === opts.last2 : true;
      const routingOk = opts.routing
        ? '' + (b as any).ABANumber === opts.routing
        : true;

      return nickOk && lastOk && routingOk && b.ACHWalletID != null;
    };

    // quick first refresh
    await this.refreshBanksAndSelect(match);
    if (this.paymentForm.get('existingBankId')?.value) return;

    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, intervalMs));
      await this.refreshBanksAndSelect(match);
      if (this.paymentForm.get('existingBankId')?.value) return;
    }
  }

  /** Card number signal */
  readonly cardNumberSig = toSignal(
    this.paymentForm.get('cardNumber')!.valueChanges,
    {
      initialValue: this.paymentForm.get('cardNumber')!.value,
    }
  );

  /**
   * Constructor to initialize form and set up validation effects
   */
  constructor() {
    // --- Input hard-caps/sanitizers for card fields ---
    // 1) Expiration: keep digits, max 4 (MMYY), display as MM/YY
    this.paymentForm.get('cardExpiry')!.valueChanges.subscribe((v) => {
      if (v == null) return;
      const digits = String(v).replace(/\D/g, '').slice(0, 4);
      const formatted =
        digits.length <= 2
          ? digits
          : `${digits.slice(0, 2)}/${digits.slice(2)}`;
      if (formatted !== v) {
        this.paymentForm
          .get('cardExpiry')!
          .setValue(formatted, { emitEvent: false });
      }
    });

    // 2) ZIP: digits only, cap at 5  (change to 9 for ZIP+4, and update regex above)
    this.paymentForm.get('cardZip')!.valueChanges.subscribe((v) => {
      if (v == null) return;
      const digits = String(v).replace(/\D/g, '').slice(0, 5);
      if (digits !== v) {
        this.paymentForm.get('cardZip')!.setValue(digits, { emitEvent: false });
      }
    });

    // (Optional) If you also want to cap raw cardNumber typing (not just validate):
    this.paymentForm.get('cardNumber')!.valueChanges.subscribe((v) => {
      if (v == null) return;
      const digits = String(v).replace(/\D/g, '').slice(0, 16); // max 19 digits
      const grouped = digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim(); // 4-4-4-4-3 etc.
      if (grouped !== v) {
        this.paymentForm
          .get('cardNumber')!
          .setValue(grouped, { emitEvent: false });
      }
    });

    effect(() => {
      const method = this.paymentMethodSig();
      if (method === 'card') {
        // fire-and-forget (no blocking the UI)
        this.#ipospay.ensureLoaded().catch((err) => {
          console.error('FTD preload failed', err);
          this.#snackbar.open('Payment engine couldn’t preload.', 'Close', {
            duration: 3000,
          });
        });
      }
    });

    // Set up validation for bank account option-specific fields
    effect(() => {
      // Find the bank account option
      const bankOption = this.bankAccountOption();

      if (bankOption === 'new') {
        // If set to new, apply validators for bank account fields
        this.paymentForm
          .get('routingNumber')
          ?.setValidators([Validators.required, Validators.pattern(/^\d{9}$/)]);
        this.paymentForm
          .get('accountNumber')
          ?.setValidators([Validators.required, accountNumberValidator]);
        this.paymentForm
          .get('accountNumberConfirm')
          ?.setValidators([
            Validators.required,
            accountNumberMatchValidator(this.paymentForm.get('accountNumber')!),
          ]);
        this.paymentForm
          .get('accountType')
          ?.setValidators([Validators.required]);
        this.paymentForm.get('existingBankId')?.disable({ emitEvent: false });
        // 🔑 Ensure any prefilled wallet id is cleared so it can't leak into payload
        if (this.paymentForm.get('existingBankId')?.value != null) {
          this.paymentForm.patchValue(
            { existingBankId: null },
            { emitEvent: false }
          );
        }
      } else {
        // If set to existing, clear validators for bank account fields
        this.paymentForm.get('routingNumber')?.clearValidators();
        this.paymentForm.get('accountNumber')?.clearValidators();
        this.paymentForm.get('accountNumberConfirm')?.clearValidators();
        this.paymentForm.get('accountType')?.clearValidators();
        this.paymentForm.get('existingBankId')?.enable({ emitEvent: false });
      }

      // Update validity of bank account fields
      this.paymentForm.get('routingNumber')?.updateValueAndValidity();
      this.paymentForm.get('accountNumber')?.updateValueAndValidity();
      this.paymentForm.get('accountNumberConfirm')?.updateValueAndValidity();
      this.paymentForm.get('accountType')?.updateValueAndValidity();
    });

    effect(() => {
      // Set it to existing bank instead of use new when selecting auto pay or recurring payment
      const bankOption = this.bankAccountOption();
      if (
        bankOption === 'new' &&
        (this.paymentType() === 'autoPay' ||
          this.paymentType() === 'recurringPayment')
      ) {
        this.paymentForm.patchValue({ bankAccountOption: 'existing' });
      }
    });

    // in your constructor, when you add the control:
    // (this.paymentForm as FormGroup).addControl(
    //   'existingBankId',
    //   this.#fb.control<number | null>(null, Validators.required)
    // );

    this.paymentForm.get('paymentType')?.enable({ emitEvent: false });

    // Set up validation for payment type-specific fields
    effect(() => {
      // Find the payment type
      const paymentType = this.paymentType();
      const method = this.paymentMethodSig();

      if (paymentType === 'oneTime') {
        // Set validators for one-time payment fields
        this.paymentForm
          .get('amount')
          ?.setValidators([Validators.required, Validators.min(1)]);
        if (method === 'card') {
          this.paymentForm.get('oneTimeDate')?.clearValidators();
        } else {
          this.paymentForm
            .get('oneTimeDate')
            ?.setValidators([Validators.required]);
        }
        this.paymentForm.get('recurringAmount')?.clearValidators();
        this.paymentForm.get('frequency')?.clearValidators();
        this.paymentForm.get('dayOfWeek')?.clearValidators();
        this.paymentForm.get('monthlyStartDate')?.clearValidators();

        // Set legacy field for compatibility
        this.paymentForm.patchValue(
          { isRecurring: false },
          { emitEvent: false }
        );
      } else if (paymentType === 'autoPay') {
        this.paymentForm.get('amount')?.clearValidators();
        this.paymentForm.get('oneTimeDate')?.clearValidators();
        this.paymentForm.get('recurringAmount')?.clearValidators();
        this.paymentForm.get('frequency')?.clearValidators();
        this.paymentForm.get('dayOfWeek')?.clearValidators();
        this.paymentForm.get('monthlyStartDate')?.clearValidators();

        // Set legacy field for compatibility
        this.paymentForm.patchValue(
          {
            isRecurring: true,
            amountType: PaymentAmountType.Regular,
          },
          { emitEvent: false }
        );
      } else if (paymentType === 'recurringPayment') {
        // Set validators for recurring payment fields
        this.paymentForm
          .get('recurringAmount')
          ?.setValidators([Validators.required, Validators.min(1)]);
        this.paymentForm.get('frequency')?.setValidators([Validators.required]);
        this.paymentForm.get('amount')?.clearValidators();
        this.paymentForm.get('oneTimeDate')?.clearValidators();

        // Set legacy field for compatibility
        this.paymentForm.patchValue(
          {
            isRecurring: true,
            amountType: PaymentAmountType.Fixed,
          },
          { emitEvent: false }
        );
      }

      // Update validity of payment fields
      this.paymentForm.get('amount')?.updateValueAndValidity();
      this.paymentForm.get('oneTimeDate')?.updateValueAndValidity();
      this.paymentForm.get('recurringAmount')?.updateValueAndValidity();
      this.paymentForm.get('frequency')?.updateValueAndValidity();
    });

    // Keep validators in sync with payment METHOD (card vs bank)
    effect(() => {
      const method = this.paymentMethodSig();
      const isOneTime = this.paymentType() === 'oneTime';

      // Card fields
      if (method === 'card') {
        // apply card validators
        this.paymentForm
          .get('cardName')
          ?.setValidators(this.cardValidators.name);
        this.paymentForm
          .get('cardNumber')
          ?.setValidators(this.cardValidators.number);
        this.paymentForm
          .get('cardExpiry')
          ?.setValidators(this.cardValidators.expiry);
        this.paymentForm.get('cardCvv')?.setValidators(this.cardValidators.cvv);
        this.paymentForm.get('cardZip')?.setValidators(this.cardValidators.zip);

        // if one-time + card, clear ACH validators (we're not using them)
        if (isOneTime) {
          this.paymentForm.get('routingNumber')?.clearValidators();
          this.paymentForm.get('accountNumber')?.clearValidators();
          this.paymentForm.get('accountNumberConfirm')?.clearValidators();
          this.paymentForm.get('accountType')?.clearValidators();
          // one-time date is hidden for card -> not required
          this.paymentForm.get('oneTimeDate')?.clearValidators();
        }
      } else {
        // clear card validators
        this.paymentForm.get('cardNumber')?.clearValidators();
        this.paymentForm.get('cardExpiry')?.clearValidators();
        this.paymentForm.get('cardCvv')?.clearValidators();
        this.paymentForm.get('cardZip')?.clearValidators();

        // if one-time + bank, require a date
        if (isOneTime) {
          this.paymentForm
            .get('oneTimeDate')
            ?.setValidators([Validators.required]);
        }
      }

      // update validity for all affected fields
      [
        'cardNumber',
        'cardExpiry',
        'cardCvv',
        'cardZip',
        'routingNumber',
        'accountNumber',
        'accountNumberConfirm',
        'accountType',
        'oneTimeDate',
      ].forEach((k) =>
        this.paymentForm.get(k)?.updateValueAndValidity({ emitEvent: false })
      );
    });

    // If user flips to CARD, force paymentType to oneTime (autopay is not allowed for card)
    effect(() => {
      const method = this.paymentMethodSig();
      const pt = this.paymentType();
      if (method === 'card' && pt !== 'oneTime') {
        this.paymentForm.patchValue(
          { paymentType: 'oneTime' },
          { emitEvent: false }
        );
      }
    });

    // Set up validation for frequency-specific fields
    effect(() => {
      // Find the frequency
      const frequency = this.frequency();

      if (frequency === 'weekly') {
        // If frequency is weekly, set validators for day of week
        this.paymentForm.get('dayOfWeek')?.setValidators([Validators.required]);
        this.paymentForm.get('monthlyStartDate')?.clearValidators();
      } else if (frequency === 'monthly') {
        // If frequency is monthly, set validators for monthly start date
        this.paymentForm
          .get('monthlyStartDate')
          ?.setValidators([Validators.required]);
        this.paymentForm.get('dayOfWeek')?.clearValidators();
      } else {
        // If frequency is one-time, clear validators for both
        this.paymentForm.get('dayOfWeek')?.clearValidators();
        this.paymentForm.get('monthlyStartDate')?.clearValidators();
      }

      // Update validity of frequency-specific fields
      this.paymentForm.get('dayOfWeek')?.updateValueAndValidity();
      this.paymentForm.get('monthlyStartDate')?.updateValueAndValidity();
    });

    // Only load existing recurring data when the ROUTE explicitly indicates edit mode
    effect(() => {
      const loan = this.loan();
      if (
        this.routeWantsRecurringEdit &&
        loan?.paymentSettings.recurringPaymentExists &&
        loan?.recurringPayment
      ) {
        this.populateFormWithRecurringPaymentData(loan.recurringPayment);
      } else {
        this.resetEditingState();
      }
    });
  }

  openManagePaymentMethods() {
    const dialogRef = this.dialog.open(ManagePaymentMethodsDialogComponent, {
      width: '600px',
      height: '100vh',
      maxHeight: '100vh',
      position: { top: '0', right: '0' },
      panelClass: 'side-dialog',
      data: { accounts: this.banks },
    });

    // dialog returns either a single new Bank or the full updated array after removal/close
    dialogRef.afterClosed().subscribe((result: Bank | Bank[] | undefined) => {
      if (!result) return;

      try {
        if (Array.isArray(result)) {
          // Replace with authoritative list (e.g., after removal / cancel)
          this.banks = result;

          // Clear selection if the currently selected ID no longer exists
          const existingId = this.paymentForm.get('existingBankId')?.value as
            | number
            | null;
          if (
            existingId &&
            !this.banks.some((b) => b.ACHWalletID === existingId)
          ) {
            this.paymentForm.patchValue({ existingBankId: null });
          }

          // Light refresh to stay in sync with backend
          void this.refreshBanksAndSelect().catch((err) =>
            console.error('refreshBanksAndSelect failed:', err)
          );
        } else {
          // Single new/edited bank
          const already = this.banks.some(
            (b) => b.ACHWalletID === result.ACHWalletID
          );
          if (!already) this.banks.push(result);

          if (result.ACHWalletID != null) {
            // We got a real wallet id; select it and ensure we're synced with backend
            this.paymentForm.patchValue({
              existingBankId: result.ACHWalletID,
              bankAccountOption: 'existing',
            });
            void this.refreshBanksAndSelect(
              (b) => '' + b.ACHWalletID === '' + result.ACHWalletID
            ).catch((err) =>
              console.error('refreshBanksAndSelect (select new) failed:', err)
            );
          } else {
            // No ACHWalletID yet — start polling until backend assigns one and then select it
            this.paymentForm.patchValue({ bankAccountOption: 'existing' });
            void this.pollForACHWalletId({
              last2: ('' + result.AccountNumber).slice(-2),
              routing: (result as any).ABANumber ?? '',
              nickname: result.bankName ?? '',
            }).catch((err) => console.error('pollForACHWalletId failed:', err));
          }
        }
      } catch (e) {
        console.error('manage payment methods result handling failed:', e);
      }
    });
  }

  /**
   * On initialized component state:
   * - Check for auto pay query parameter and set initial form values accordingly
   * - Set bank as default payment type
   * - Subscribe to bank lookup observable to fetch bank details
   */
  ngOnInit(): void {
    // Initial lock/unlock based on current URL (edit vs make-payment)
    const initialBT = this.#route.snapshot.queryParamMap.get('billingType');
    const qp = this.#route.snapshot.queryParams;
    const autoPayParam = qp['autoPay'];
    const paymentIdParam = qp['paymentId'];
    const walletIdParam = this.#route.snapshot.queryParamMap.get('walletId');
    this.initialWalletId = walletIdParam ? parseInt(walletIdParam, 10) : null;

    // Lock/initialize based on billing type (handles autopay vs fixed internally)
    this.setPaymentTypeFromBillingType(initialBT, autoPayParam);

    this.routeWantsRecurringEdit = autoPayParam === 'true';

    // If we have paymentId and NOT autoPay, treat this as editing a one-time payment
    if (paymentIdParam && autoPayParam !== 'true') {
      this.editingOneTimePaymentId = Number(paymentIdParam);

      // TODO: implement getPendingPaymentById in PaymentService (or use UpcomingPaymentsService)
      this.#paymentService
        .getPendingPaymentById(this.editingOneTimePaymentId)
        .subscribe((p) => {
          if (!p) return;
          // Prefill one-time form based on pending payment data
          this.paymentForm.patchValue({
            paymentType: 'oneTime',
            isRecurring: false,
            amount: p.paymentAmount != null ? String(p.paymentAmount) : '',
            oneTimeDate: p.endDate ? new Date(p.endDate) : null,
            bankAccountOption: 'existing',
            //existingBankId: (p.id != null) ? String(p.id) : ''
          });
        });
    }

    // Set bank as default payment type
    this.paymentForm.patchValue({ type: this.PaymentType.Bank });

    // Load banks, then preselect wallet if present
    this.#bankService.getBanks().subscribe({
      next: (banks) => {
        this.banks = banks;
        this.disableSaveBank = banks.length > 0;

        // If URL gave us a walletId, prefer that
        if (this.initialWalletId) {
          const found = banks.find(
            (b) => b.ACHWalletID === this.initialWalletId
          );
          if (found?.ACHWalletID != null) {
            this.paymentForm.patchValue(
              {
                existingBankId: found.ACHWalletID, // ⬅️ numeric
                bankAccountOption: 'existing',
              },
              { emitEvent: false }
            );
            return; // done
          }
        }

        // Otherwise, keep your current “first bank” default if any
        const first = banks[0];
        this.paymentForm
          .get('existingBankId')!
          .setValue(first?.ACHWalletID ?? null); // ⬅️ numeric or null
      },
      error: (err) => console.error('Failed to load banks', err),
    });

    // React to future URL changes (e.g., user navigates from Edit to Make Payment)
    this.#route.queryParamMap
      .pipe(
        map((qp) => qp.get('billingType')),
        distinctUntilChanged()
      )
      .subscribe((bt) => this.setPaymentTypeFromBillingType(bt));
    // Also keep date validator synced when method changes at runtime while one-time is selected
    this.paymentForm.get('paymentMethod')!.valueChanges.subscribe((method) => {
      if (this.paymentForm.get('paymentType')!.value === 'oneTime') {
        if (method === 'card') {
          this.paymentForm.get('oneTimeDate')?.clearValidators();
        } else {
          this.paymentForm
            .get('oneTimeDate')
            ?.setValidators([Validators.required]);
        }
        this.paymentForm
          .get('oneTimeDate')
          ?.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  /**
   * Reset the editing state when switching payment types or creating new payments
   */
  private resetEditingState(): void {
    // Reset editing state and re-enable fields for new payments
    this.isEditingExistingRecurringPayment = false;

    // Re-enable all form fields
    this.paymentForm.get('accountNumber')?.enable();
    this.paymentForm.get('routingNumber')?.enable();
  }

  // normalize "MM/YY" -> "MMYY"
  private compactExpiry(mmYY: string): string {
    if (!mmYY) return '';
    const digits = mmYY.replace(/\D/g, '').slice(0, 4);
    if (digits.length < 4) return '';
    return digits;
  }

  // digits only (strip spaces)
  private digitsOnly(s: string | null | undefined): string {
    return (s ?? '').replace(/\D/g, '');
  }

  /** Label for the review “Payment Method” row */
  readonly reviewMethodLabel = computed(() =>
    this.paymentMethodSig() === 'card' ? 'Debit Card' : 'Bank Account (ACH)'
  );

  /** Masked card last4 for Review */
  readonly cardLast4 = computed(() => {
    const raw = this.cardNumberSig() ?? '';
    const digits = String(raw).replace(/\D/g, '');
    return digits.slice(-4);
  });

  /** Review amount: includes 2% fee when method is card */
  readonly reviewAmount = computed(() => {
    const raw = this.amountSig() as any;
    const amount = typeof raw === 'number' ? raw : parseFloat(raw || '0');
    if (!amount || Number.isNaN(amount)) return 0;

    return this.paymentMethodSig() === 'card'
      ? this.cardTotal() // total incl. 2% fee (already rounded)
      : +(+amount).toFixed(2); // plain amount for bank
  });

  /** Review date: for one-time card, show today's date; for bank, show chosen date */
  readonly reviewDate = computed(() => {
    if (this.paymentType() !== 'oneTime') return null; // handled elsewhere
    if (this.paymentMethodSig() === 'card') return this.today; // today for card
    return this.paymentForm.get('oneTimeDate')!.value ?? null; // bank uses selected date
  });

  // today at local noon to sidestep TZ weirdness
  private todayLocal(): Date {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    return d;
  }

  /**
   * Populate the form with existing recurring payment data
   *
   * @param recurringPayment Recurring payment details to populate the form
   */
  private populateFormWithRecurringPaymentData(
    recurringPayment: RecurringPaymentDetails
  ): void {
    // Mark that we are editing an existing recurring payment
    this.isEditingExistingRecurringPayment = true;

    // Map to new form structure
    const paymentType = recurringPayment.paymentAmount
      ? 'recurringPayment'
      : 'autoPay';

    this.paymentForm.patchValue({
      paymentType: paymentType,
      recurringAmount: recurringPayment.paymentAmount?.toString() || '',
      type: recurringPayment.paymentMethod,
      isRecurring: true,
      period: recurringPayment.frequency,
      dateRange: {
        start: recurringPayment.startDate,
        end: recurringPayment.endDate,
      },
    });

    const bt = this.#route.snapshot.queryParamMap.get('billingType');
    this.setPaymentTypeFromBillingType(bt);

    // Populate payment method specific fields based on stored data
    if (recurringPayment.paymentMethod === PaymentType.Bank) {
      this.paymentForm.patchValue({
        accountNumber: recurringPayment.accountNumber || '', // Already obfuscated from backend
        routingNumber: '*********', // Always mask routing number for security
      });

      // Disable bank account fields for security - they show masked values but cannot be edited
      this.paymentForm.get('accountNumber')?.disable();
      this.paymentForm.get('routingNumber')?.disable();
      this.paymentForm.get('accountNumberConfirm')?.disable();
    }
  }

  /**
   * Submit the payment form
   *
   * @returns Promise<void>
   */
  async submitPayment() {
    if (!this.paymentForm.valid) return;

    this.isProcessingPayment = true;
    try {
      // Use value (NOT getRawValue) so disabled controls (e.g., existingBankId when "new") don't sneak in
      const formValues = this.paymentForm
        .value as typeof this.paymentForm.value;
      const loan = this.loan();
      const method = this.paymentForm.get('paymentMethod')!.value;

      // ───── DEBIT CARD FLOW (early exit) ──────────────────────────────────────
      if (method === 'card') {
        // basic guards (validators already applied elsewhere)
        const ccNumber = String(formValues.cardNumber ?? '').replace(/\D/g, '');
        const ccExpiry = String(formValues.cardExpiry ?? '')
          .replace(/\D/g, '')
          .slice(0, 4); // MMYY
        const ccCvv = String(formValues.cardCvv ?? '').replace(/\D/g, '');
        const zip = String(formValues.cardZip ?? '')
          .replace(/\D/g, '')
          .slice(0, 5);

        if (!loan?.number) throw new Error('Missing loan number');
        if (!formValues.amount)
          throw new Error('Amount required for card payment');

        await firstValueFrom(
          this.#paymentService.processDebitCardPayment({
            loanNumber: loan.number,
            ccNumber,
            ccExpiry, // e.g., "1228"
            ccCvv,
            amount: +formValues.amount,
            zip,
          })
        );

        this.paymentConfirmed = true;
        this.#cdr.detectChanges();
        this.currentStep = 2;
        return; // ✅ do not run ACH logic or save-bank prompts
      }

      // Determine whether we should ask to save the bank:
      // disableSaveBank === true if getBanks() returned any accounts
      const usingNewBank =
        this.paymentForm.get('bankAccountOption')?.value === 'new';
      let addToWallet = false;

      if (usingNewBank) {
        const dialogRef = this.dialog.open(ConfirmAddBankDialogComponent, {
          data: {
            message:
              'Would you like to add this bank account to your wallet for future payments?',
          },
        });
        addToWallet = await firstValueFrom(dialogRef.afterClosed());
      }

      // if (!this.disableSaveBank) {
      //   const dialogRef = this.dialog.open(ConfirmAddBankDialogComponent, {
      //     data: { message: 'Would you like to add this bank account to your wallet for future payments?' }
      //   });
      //   addToWallet = await firstValueFrom(dialogRef.afterClosed());
      // }

      // ❌ Skip “save bank?” prompts when paying by card
      if (method !== 'card' && usingNewBank) {
        const dialogRef = this.dialog.open(ConfirmAddBankDialogComponent, {
          data: {
            message:
              'Would you like to add this bank account to your wallet for future payments?',
          },
        });
        addToWallet = await firstValueFrom(dialogRef.afterClosed());
      }
      if (method !== 'card' && !this.disableSaveBank) {
        const dialogRef = this.dialog.open(ConfirmAddBankDialogComponent, {
          data: {
            message:
              'Would you like to add this bank account to your wallet for future payments?',
          },
        });
        addToWallet = await firstValueFrom(dialogRef.afterClosed());
      }

      // Map new form values to legacy PaymentForm structure
      let amount: number | null = null;
      let isRecurring = false;
      let amountType = PaymentAmountType.Fixed;

      if (formValues.paymentType === 'oneTime') {
        amount = formValues.amount ? parseFloat(formValues.amount) : null;
        isRecurring = false;
      } else if (formValues.paymentType === 'autoPay') {
        amount = loan?.nextPaymentDueAmount || null;
        isRecurring = true;
        amountType = PaymentAmountType.Regular;
      } else if (formValues.paymentType === 'recurringPayment') {
        amount = formValues.recurringAmount
          ? parseFloat(formValues.recurringAmount)
          : null;
        isRecurring = true;
        amountType = PaymentAmountType.Fixed;
      }

      // Only honor existingBankId when option is explicitly "existing"
      const usingExisting =
        this.paymentForm.get('bankAccountOption')?.value === 'existing';
      const existingBankIdNum = usingExisting
        ? formValues.existingBankId ?? undefined
        : undefined;
      // ---- NEW: compute one canonical startDate for *all* payment types ----
      const weekdayMap: Record<string, number> = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6,
      };

      let startDate: Date | null = null;

      if (isRecurring) {
        if (formValues.frequency === 'weekly' && formValues.dayOfWeek) {
          startDate = getNextWeekdayDate(weekdayMap[formValues.dayOfWeek]);
        } else if (
          formValues.frequency === 'monthly' &&
          formValues.monthlyStartDate
        ) {
          startDate = formValues.monthlyStartDate;
        } else {
          // fallback for autopay with no explicit start: today
          startDate = new Date();
        }
      } else {
        // one-time: use selected date (required by validators), fallback today
        // one-time:
        // - bank: use selected date (required by validators)
        // - card: date is hidden/not required -> use today
        if (this.paymentForm.get('paymentMethod')!.value === 'card') {
          startDate = new Date();
        } else {
          startDate = formValues.oneTimeDate ?? new Date();
        }
      }

      // normalize to avoid timezone “previous day” issues
      startDate = normalizeLocalDate(startDate);

      // derive period consistently
      const period =
        formValues.frequency === 'weekly'
          ? PaymentPeriod.Weekly
          : formValues.frequency === 'monthly'
          ? PaymentPeriod.Monthly
          : PaymentPeriod.OneTime;

      const paymentData: PaymentForm = {
        amount,
        type: PaymentType.Bank,
        isRecurring,
        amountType,
        loanId: this.id(),
        // 🔒 Only include existingBankId if user chose "Use Existing"
        ...(usingExisting &&
          existingBankIdNum != null && { existingBankId: existingBankIdNum }),
        ...(formValues.accountNumber && {
          accountNumber: formValues.accountNumber,
        }),
        ...(formValues.accountType && {
          accountType: formValues.accountType as AccountType,
        }),
        ...(formValues.routingNumber && {
          routingNumber: formValues.routingNumber,
        }),

        // ✅ Always send a dateRange with only the start
        ...(startDate && { dateRange: { start: startDate, end: null } }),

        period,
        billRegularAmount: formValues.paymentType === 'autoPay',
      };

      // Only create a wallet if user chose to AND save-bank hasn't been disabled
      if (addToWallet) {
        const numericAccountType =
          formValues.accountType === AccountType.Checking ? 0 : 1;
        const walletBody = {
          AccountType: numericAccountType,
          Nickname: `Loan ${loan?.number} – ${formValues.accountType}`,
          AccountNumber: formValues.accountNumber!,
          ABANumber: formValues.routingNumber!,
          Validated: 0,
          Active: 1,
        } as const;
        await firstValueFrom(this.#paymentService.createWallet(walletBody));
      }

      if (addToWallet) {
        const numericAccountType =
          formValues.accountType === AccountType.Checking ? 0 : 1;
        const walletBody = {
          AccountType: numericAccountType,
          Nickname: `Loan ${loan?.number} – ${formValues.accountType}`,
          AccountNumber: formValues.accountNumber!,
          ABANumber: formValues.routingNumber!,
          Validated: 0,
          Active: 1,
        } as const;

        await firstValueFrom(this.#paymentService.createWallet(walletBody));

        // ⬇️ NEW: poll until backend assigns ACHWalletID, then select it
        await this.pollForACHWalletId({
          last2: (formValues.accountNumber ?? '').slice(-2),
          routing: formValues.routingNumber ?? '',
          nickname: `Loan ${loan?.number}`,
        });
      }

      // Process or update the payment
      const isEditingRecurring = this.isEditingExistingRecurringPayment;
      const recurringId = loan?.recurringPayment?.id; // <- safely optional

      // ❗ NEW: if we navigated here with ?paymentId=... (and NOT autoPay),
      // we're editing a ONE-TIME payment – do a PUT.
      if (!isRecurring && this.editingOneTimePaymentId != null) {
        await firstValueFrom(
          this.#paymentService.updateOneTimePayment(
            this.editingOneTimePaymentId,
            paymentData
          )
        );
      } else if (isEditingRecurring && recurringId) {
        await firstValueFrom(
          this.#paymentService.updateRecurringPayment(recurringId, paymentData)
        );
      } else {
        await firstValueFrom(this.#paymentService.processPayment(paymentData));
      }

      this.paymentConfirmed = true;
      this.#cdr.detectChanges();
      this.currentStep = 2;
    } catch (error) {
      console.error('Payment processing failed:', error);

      const config: MatSnackBarConfig = {
        horizontalPosition: 'right',
        verticalPosition: 'bottom',
        duration: 5000,
        panelClass: ['snack-bar-error'],
      };

      // If bank account show this error, otherwise show a different error for debit card
      if (this.paymentMethodSig() === 'bank') {
        this.#snackbar.open(
          'Unable to save bank account. Please check your details and try again.',
          'Close',
          config
        );
      } else {
        this.#snackbar.open(
          'Unable to process debit card payment. Please check your details and try again.',
          'Close',
          config
        );
      }
    } finally {
      this.isProcessingPayment = false;
    }
  }

  /**
   * Open up the autopay disclosure dialog using the dialog service
   */
  public openAutopayDisclosure(): void {
    this.#dialogService.openAuthorizationDialog();
  }

  /** 
   * Open up the error resolution disclosure
   */
  public openErrorResolutionDisclosure(): void {
    this.#dialogService.openErrorResolutionDisclosure();
  }
}

// add near your other validators
function cardPanValidator(requireLuhn = false): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = (control.value ?? '') as string;
    const digits = raw.replace(/\D/g, '');
    if (digits.length < 13 || digits.length > 19) return { length: true };
    if (!requireLuhn) return null;

    // simple Luhn
    let sum = 0,
      dbl = false;
    for (let i = digits.length - 1; i >= 0; i--) {
      let n = +digits[i];
      if (dbl) {
        n *= 2;
        if (n > 9) n -= 9;
      }
      sum += n;
      dbl = !dbl;
    }
    return sum % 10 === 0 ? null : { luhn: true };
  };
}

// helper to get next date for a given weekday (0=Sunday…6=Saturday)
function getNextWeekdayDate(targetDay: number): Date {
  const today = new Date();
  const diff = (targetDay + 7 - today.getDay()) % 7 || 7;
  today.setDate(today.getDate() + diff);
  return today;
}
function normalizeLocalDate(d: Date): Date {
  const n = new Date(d);
  // set to noon local time to avoid UTC shifting into prior day
  n.setHours(12, 0, 0, 0);
  return n;
}
